import { useState, type ReactNode } from 'react';
import { Check, Plus } from 'lucide-react';
import { TASK_LABEL_COLORS, type TaskLabel, type TaskLabelColor, type TaskMember } from '@shared/types/tasks';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { LabelChip, memberName } from './task-chips';
import { MemberAvatar } from '@/components/team/MemberAvatar';

/*
 * The people and label pickers, free of the router and stores so the Chrome extension (extension/)
 * uses the same ones. TaskPickers.tsx wires LabelPicker's create to the board.
 */

/** Toggle a person on a task; closes after each pick (the + opens it again for another). */
export function AssigneePicker({
  members,
  selected,
  onChange,
  defaultOpen = false,
  onOpenChange,
  children,
}: {
  members: TaskMember[];
  selected: string[];
  onChange: (ids: string[]) => void;
  /** Open as soon as it appears (picked from a + menu). */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}) {
  const [open, setOpenState] = useState(defaultOpen);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search people…" />
          <CommandList>
            <CommandEmpty>No one found.</CommandEmpty>
            <CommandGroup>
              {members.map((m) => {
                const on = selected.includes(m.user_id);
                return (
                  <CommandItem
                    key={m.user_id}
                    value={`${memberName(m)} ${m.email}`}
                    onSelect={() => {
                      onChange(on ? selected.filter((id) => id !== m.user_id) : [...selected, m.user_id]);
                      setOpen(false);
                    }}
                  >
                    <MemberAvatar name={memberName(m)} photourl={m.photourl} size="sm" />
                    <span className="flex-1 truncate">{memberName(m)}</span>
                    {on ? <Check className="size-4" /> : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Toggle Labels on a task; typing a new name offers to create it. Closes after each pick, like people. */
export function LabelPicker({
  labels,
  selected,
  onChange,
  onCreate,
  defaultOpen = false,
  onOpenChange,
  children,
}: {
  labels: TaskLabel[];
  selected: string[];
  onChange: (ids: string[]) => void;
  /** Makes the label; null when it couldn't (the name is taken, or the save failed). */
  onCreate: (name: string, color: TaskLabelColor) => TaskLabel | null | Promise<TaskLabel | null>;
  /** Open as soon as it appears (picked from a + menu). */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}) {
  const [open, setOpenState] = useState(defaultOpen);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const [search, setSearch] = useState('');
  const trimmed = search.trim();
  const close = () => {
    setOpen(false);
    setSearch('');
  };
  const exists = labels.some((l) => l.name.toLowerCase() === trimmed.toLowerCase());

  const create = async () => {
    const color = TASK_LABEL_COLORS[(labels.length + 1) % TASK_LABEL_COLORS.length];
    const label = await onCreate(trimmed, color);
    if (!label) return;
    onChange([...selected, label.id]);
    close();
  };

  return (
    <Popover open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search or create a label…" value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{trimmed ? null : 'No labels yet. Type a name to create one.'}</CommandEmpty>
            <CommandGroup>
              {labels.map((label) => {
                const on = selected.includes(label.id);
                return (
                  <CommandItem
                    key={label.id}
                    value={label.name}
                    onSelect={() => {
                      onChange(on ? selected.filter((id) => id !== label.id) : [...selected, label.id]);
                      close();
                    }}
                  >
                    <LabelChip label={label} />
                    <span className="flex-1" />
                    {on ? <Check className="size-4" /> : null}
                  </CommandItem>
                );
              })}
              {trimmed && !exists ? (
                <CommandItem value={`create ${trimmed}`} onSelect={() => void create()}>
                  <Plus className="size-4" />
                  Create “{trimmed}”
                </CommandItem>
              ) : null}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
